import React from "react"
import { withFormik, Form, Field, FastField } from "formik"
import { Redirect } from "react-router-dom"
import { connect } from "react-redux"
import * as Yup from "yup"
import moment from "moment"
import {
  MobileNumberField,
  DateField,
  TextField,
  DropdownField
} from "../../common/Fields"
import "./UserInformationForm.css"
import { API } from "../../api"
import { logInUser } from "../../_actions/userActionCreator"
import { showNotificationWithTimeout } from "../../_actions/adminAlertActionCreator"
import {
  ROUTES,
  getCachedData,
  cacheData,
  sessionData
} from "../../common/utils"

const UserInformationBaseForm = ({
  values,
  errors,
  touched,
  isSubmitting,
  hasUserInfo,
  hasLatLng,
  location
}) => {
  if (!hasUserInfo) {
    return (
      <div className="UserInformationForm">
        <Form>
          <label>Mobile Number</label>
          <MobileNumberField
            groupName="mobile"
            countryCodeName="countryCode"
            mobileNumberName="mobileNumber"
            errors={errors}
            touched={touched}
            values={values}
          />
          <div className="split">
            <span>
              <label>Date of Birth (Optional)</label>
              <Field
                component={DateField}
                name="birthday"
                value={values.birthday}
                maxDate={moment()}
                minDate={null}
                selected={null}
              />
            </span>
            <span>
              <label>Gender (Optional)</label>
              <FastField
                component={DropdownField}
                name="gender"
                value={values.gender}
                defaultValue={{ value: "", label: "" }}
                options={[
                  { value: "m", label: "Male" },
                  { value: "f", label: "Female" },
                  { value: "o", label: "Prefer not to say" }
                ]}
                type="text"
              />
            </span>
          </div>
          <hr />
          <div className="split">
            <span>
              <label>First Name</label>
              <FastField
                component={TextField}
                name="first_name"
                type="text"
                value={values.first_name}
              />
            </span>
            <span>
              <label>Last Name</label>
              <FastField
                component={TextField}
                name="last_name"
                type="text"
                value={values.last_name}
              />
            </span>
          </div>
          <label>Email Address</label>
          <FastField
            component={TextField}
            name="email"
            type="email"
            values={values.email}
            disabled={true}
          />
          <div className="submitForm" disabled={isSubmitting}>
            <button type="submit">{isSubmitting ? "Saving" : "Next"}</button>
          </div>
        </Form>
      </div>
    )
  } else if (!hasLatLng) {
    return (
      <Redirect
        to={{
          pathname: ROUTES.CREATE_ACCOUNT,
          state: { from: location }
        }}
      />
    )
  }
  return (
    <Redirect
      to={{
        pathname: ROUTES.ADMIN,
        state: { from: location }
      }}
    />
  )
}

const UserInformationForm = withFormik({
  mapPropsToValues: ({ account }) => ({
    mobile: {
      countryCode: "",
      mobileNumber: ""
    },
    birthday: "",
    gender: "",
    first_name: (account && account.first_name) || "",
    last_name: (account && account.last_name) || "",
    email: (account && account.email) || ""
  }),
  validationSchema: Yup.object().shape({
    birthday: Yup.string(),
    gender: Yup.string()
      .max(1, "Please enter a valid gender.")
      .matches(/(m|M|F|f|o)/, "Please enter a valid gender."),
    mobile: Yup.object().shape({
      countryCode: Yup.string()
        .matches(
          /^($)|(\+?\d{1,3}$|\d{1,4})/,
          "Please enter a valid country code."
        )
        .max(5, "Please enter a valid country code.")
        .required("Please enter a country code."),
      mobileNumber: Yup.string()
        .matches(/^($)|([0-9]{4,13})/, "Please enter a valid mobile number.")
        .max(14, "Please enter a valid mobile number.")
        .required("Please enter a mobile number.")
    }),
    first_name: Yup.string().required("First name is a required field."),
    last_name: Yup.string().required("Last name is a required field."),
    email: Yup.string().required("Email is a required field.")
  }),
  handleSubmit: async (values, { props, setErrors, setSubmitting }) => {
    const userInfo = {
      first_name: values.first_name || null,
      last_name: values.last_name || null,
      email: values.email || null,
      mobile: values.mobile.mobileNumber.trim() || null,
      mobile_country_code: values.mobile.countryCode.trim() || null,
      birthday: moment(values.birthday).isValid()
        ? moment(values.birthday).format("YYYY-MM-DD")
        : null,
      gender: values.gender || null,
      photo_url: null
    }
    try {
      await API.put("accounts/profiles", JSON.stringify(userInfo))
      const storeData = {
        ...props.currentUser,
        account: { ...props.account, ...userInfo }
      }
      const cachedUser = getCachedData("Qarabao_User")
      if (cachedUser) {
        cacheData("Qarabao_User", storeData)
      }
      sessionData("Qarabao_User", storeData)
      props.logInUser(storeData)
    } catch (error) {
      if (error.response) {
        console.log(error.response)
        const { data } = error.response
        if (data && data.code === "Something went wrong!") {
          setErrors({ name: "Account does not exist." })
        } else if (data && data.code === "customers/invalid-mobile-number") {
          setErrors({
            mobile: "Invalid mobile number"
          })
        } else {
          props.showAlert({
            message: "Something went wrong.",
            type: "error",
            consumer: "userprofile"
          })
        }
      } else if (error.request) {
        props.showAlert({
          message: "Network error, connection refused by server.",
          type: "error",
          consumer: "settings"
        })
      }
    }
    setSubmitting(false)
  }
})(UserInformationBaseForm)

const mapStateToProps = state => {
  const { account, address } = state.user
  let userInfoStatus =
    account &&
    account.mobile !== "undefined" &&
    account.mobile !== "" &&
    account.mobile !== null &&
    (account.mobile_country_code !== "undefined" &&
      account.mobile_country_code !== "" &&
      account.mobile_country_code !== null)
      ? true
      : false
  let LatLngStatus =
    address &&
    address.lng !== "undefined" &&
    address.lng !== "" &&
    address.lng !== null &&
    (address.lat !== "undefined" && address.lat !== "" && address.lat !== null)
      ? true
      : false
  return {
    account,
    currentUser: state.user,
    hasUserInfo: userInfoStatus,
    hasLatLng: LatLngStatus
  }
}

const mapDispatchToProps = dispatch => ({
  showAlert: alert => {
    showNotificationWithTimeout(dispatch, alert)
  },
  logInUser: user => dispatch(logInUser(user))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserInformationForm)
